// src/components/common/FriendlyAnalysis.js - Friendly data presentation for AI and fact-check results
import React from 'react';
import { useTranslation } from 'react-i18next';

const FriendlyAnalysis = ({ data, type = 'ai' }) => {
  const { t } = useTranslation();

  if (!data) return null;

  let parsedData;
  try {
    parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  } catch (e) {
    console.error('Failed to parse analysis data:', e);
    return <p className="text-sm text-red-500 italic">Data could not be displayed correctly.</p>;
  }

  // Handle Fact Check results
  if (type === 'fact_check') {
    const matches = Array.isArray(parsedData.matches) ? parsedData.matches : [];
    return (
      <div className="space-y-3">
        <div className="mt-2">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            {t('dashboard.factCheck.matchingSources', { defaultValue: 'Related articles' })}
          </h4>

          {matches.length > 0 ? (
            <div className="space-y-2">
              {matches.slice(0, 5).map((match, idx) => (
                <div key={idx} className="text-sm border-l-2 border-gray-200 dark:border-gray-700 pl-3 py-1">
                  <a href={match.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline font-medium block">
                    {match.title}
                  </a>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {match.source}{match.published_at ? ` • ${new Date(match.published_at).toLocaleDateString()}` : ''}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('dashboard.factCheck.noMatches', { defaultValue: 'No related articles found.' })}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Handle standard AI analysis (Hate Speech, AI Detection)
  return (
    <div className="space-y-3">
      {/* Why we concluded this / Based on + linked sources */}
      {(parsedData.explanation || (parsedData.references && parsedData.references.length > 0)) && (
        <div className="mt-3 p-3 bg-shayek-primary/5 dark:bg-shayek-dark-100 rounded-lg border border-shayek-primary/20 dark:border-shayek-dark-300">
          <h4 className="text-xs font-semibold text-shayek-light-800 dark:text-shayek-dark-700 uppercase tracking-wider mb-2">
            {t('dashboard.whyWeConcluded', 'Why we concluded this:')}
          </h4>
          {parsedData.explanation && (
            <p className="text-sm text-shayek-light-700 dark:text-shayek-dark-600 mb-2">
              {parsedData.explanation}
            </p>
          )}
          <h4 className="text-xs font-semibold text-shayek-light-800 dark:text-shayek-dark-700 uppercase tracking-wider mb-1 mt-2">
            {t('common.basedOn', 'Based on')} / {t('common.references', 'References')}
          </h4>
          {parsedData.references && parsedData.references.length > 0 ? (
            <ul className={`space-y-1 ${parsedData.references.length > 4 ? 'max-h-64 overflow-y-auto pr-1' : ''}`}>
              {parsedData.references.map((ref, idx) => (
                <li key={idx}>
                  {ref.url ? (
                    <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-sm text-shayek-primary dark:text-shayek-red-light hover:underline">
                      {ref.source || ref.title || ref.url}
                    </a>
                  ) : (
                    <span className="text-sm text-shayek-light-700 dark:text-shayek-dark-300">
                      {ref.source || ref.title || '—'}
                    </span>
                  )}
                  {ref.note && <span className="text-xs text-gray-500 dark:text-gray-400 block">{ref.note}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-shayek-light-500 dark:text-shayek-dark-600 italic">
              {t('dashboard.noReferences', 'No external references for this analysis.')}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {parsedData.confidence !== undefined && (
          <div>
            <span className="text-xs text-gray-500 dark:text-shayek-dark-600 block">{t('journalist.summarize.confidence')}</span>
            <span className="text-sm font-medium text-gray-900 dark:text-shayek-dark-900">{(parsedData.confidence * 100).toFixed(1)}%</span>
          </div>
        )}
        {parsedData.severity && (
          <div>
            <span className="text-xs text-gray-500 dark:text-shayek-dark-600 block">Severity Level</span>
            <span className={`text-sm font-medium capitalize ${
              parsedData.severity === 'high' || parsedData.severity === 'critical' 
                ? 'text-red-600 dark:text-red-400' :
              parsedData.severity === 'medium' 
                ? 'text-orange-600 dark:text-orange-400' 
                : 'text-green-600 dark:text-green-400'
            }`}>
              {parsedData.severity}
            </span>
          </div>
        )}
      </div>

      {parsedData.categories && parsedData.categories.length > 0 && (
        <div>
          <span className="text-xs text-gray-500 dark:text-shayek-dark-600 block mb-1">{t('journalist.summarize.detectedCategories')}</span>
          <div className="flex flex-wrap gap-1">
            {parsedData.categories.map((cat, idx) => {
              const raw = String(cat || '').trim();
              const fallback = raw ? raw.replace(/_/g, ' ') : 'Unknown';
              const label = raw
                ? t(`report.categories.${raw}`, { defaultValue: fallback })
                : fallback;
              return (
                <span
                  key={idx}
                  className="px-2 py-0.5 bg-shayek-primary/10 text-shayek-primary dark:bg-orange-900/40 dark:text-orange-200 rounded text-xs font-medium border border-shayek-primary/20 dark:border-orange-500/60"
                >
                  {label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {parsedData.keywords && parsedData.keywords.length > 0 && (
        <div className="mt-2">
          <span className="text-xs text-gray-500 dark:text-shayek-dark-600 block mb-1">Trigger Words/Flags</span>
          <div className="flex flex-wrap gap-1">
            {parsedData.keywords.map((word, idx) => (
              <span
                key={idx}
                className="px-2 py-0.5 bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-200 rounded text-xs font-medium border border-red-100 dark:border-red-700"
              >
                {word}
              </span>
            ))}
          </div>
        </div>
      )}

      {parsedData.detected_patterns && parsedData.detected_patterns.length > 0 && (
        <div className="mt-2">
          <span className="text-xs text-gray-500 dark:text-shayek-dark-600 block mb-1">{t('common.detectedPatterns', 'Detected Patterns')}</span>
          <div className="flex flex-wrap gap-1">
            {parsedData.detected_patterns.map((p, idx) => (
              <span
                key={idx}
                className="px-2 py-0.5 bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200 rounded text-xs font-medium border border-purple-200 dark:border-purple-700"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendlyAnalysis;

